import {
    Box,
    Heading,
    Text,
    Flex,
    Avatar,
    Button,
    Grid,
    Stack,
    Badge,
    useDisclosure
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FaEdit, FaDownload, FaTools } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { GetUserTypeOptionByValue } from '../../components/options/GetOptionByValue';
import PdfExportModal from '../admin/user/modal/PdfExportModal';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../hooks/useToast';

export default function UserDashboard() {
    const [user, setUser] = useState({});
    const { get } = useApi();
    const { showError } = useToast();
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedTemplate, setSelectedTemplate] = useState('basic');
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        getProfileData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getProfileData = async () => {
        try {
            const response = await get('/me/profile');

            if (response.isSuccess === false) {
                showError('データの取得に失敗しました');
                return;
            }

            setUser(response.data || {});
        } catch (error) {
            showError('データの取得に失敗しました');
        }
    };

    const handleExport = async (userId) => {
        setIsDownloading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/me/export-pdf`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: userId,
                    template: selectedTemplate,
                }),
            });

            const blob = await res.blob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `user_${userId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            showError('PDFのエクスポートに失敗しました');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Box px={{ base: 4, md: 10 }} py={8} maxW="7xl" mx="auto">
            {/* Header */}
            <Flex mb={10} align="center" justify="space-between" flexWrap="wrap" gap={4}>
                <Flex align="center" gap={4}>
                    <Avatar size="xl" name={user.name} src={`${process.env.REACT_APP_API_URL}/storage/${user.thumb}`} />
                    <Box>
                        <Heading size="lg">{user.name}</Heading>
                        <Text color="gray.500">{user.email}</Text>
                    </Box>
                </Flex>
                <Stack direction="row" spacing={4}>
                    <Button leftIcon={<FaEdit />} colorScheme="blue" onClick={() => navigate('/profile/edit')}>
                        プロフィール編集
                    </Button>
                    <Button leftIcon={<FaTools />} colorScheme="orange" onClick={() => navigate('/my-technologies')}>
                        技術管理
                    </Button>
                    <Button leftIcon={<FaDownload />} variant="outline" colorScheme="teal" onClick={onOpen}>
                        PDF出力
                    </Button>
                </Stack>
            </Flex>

            {/* Summary Cards */}
            <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(4, 1fr)' }} gap={6} mb={10}>
                <DashboardCard label="技術数" value={user.technologies?.length || 0} />
                <DashboardCard label="ユーザタイプ" value={<Badge fontSize={'xl'} colorScheme="orange">{GetUserTypeOptionByValue(user.user_type)}</Badge>} />
                <DashboardCard label="支社" value={user.branch || '-'} />
                <DashboardCard label="部署" value={user.department || '-'} />
            </Grid>

            {/* PDF */}
            <PdfExportModal
                isOpen={isOpen}
                onClose={onClose}
                handleDownload={() => handleExport(user.id)}
                isDownloading={isDownloading}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
            />
        </Box>
    );
}

function DashboardCard({ label, value }) {
    return (
        <Box
            bg="white"
            borderRadius="xl"
            p={6}
            boxShadow="md"
            textAlign="center"
            border="1px solid"
            borderColor="gray.100"
        >
            <Text fontSize="sm" color="gray.500" mb={1}>
                {label}
            </Text>
            <Heading size="lg">{value}</Heading>
        </Box>
    );
}
